
import {settings} from "./DigitizationSettings.js";
import {settingsInGroup, visibleSettingsGroups} from "./SettingsSchema.js";
import {createCollapsibleSection} from "./CollapsibleSection.js";
import {createInfoIcon} from "../InfoTooltip.js";
import {hostDialogAvailable, requestHostConfirm} from "../HostDialog.js";

import {t} from "../i18n/index.js";
// Renders one setting's control row (auto-dispatched by `def.type`) plus its
// info icon, if it has a tooltip.
function renderControl(def){
	const row = document.createElement("div");
	row.className = "digitization-setting-row";

	if(def.type === "boolean"){
		const id = `setting_${def.key.replace(/\./g, "_")}`;
		const checkbox = document.createElement("input");
		checkbox.type = "checkbox";
		checkbox.id = id;
		checkbox.checked = !!settings.get(def.key);
		checkbox.addEventListener("change", () => settings.set(def.key, checkbox.checked));
		const label = document.createElement("label");
		label.htmlFor = id;
		label.textContent = def.label;
		row.append(checkbox, label);
	}else if(def.type === "number"){
		const id = `setting_${def.key.replace(/\./g, "_")}`;
		const value = settings.get(def.key);
		const label = document.createElement("label");
		label.htmlFor = id;
		label.style.marginRight = "6px";
		label.textContent = def.label;
		const input = document.createElement("input");
		input.type = "number";
		input.step = "any";
		input.id = id;
		input.style.width = "70px";
		// `value || ""` would blank the field for a genuine, meaningful 0
		// (e.g. vectorEditing.maxSagitta/angleTolerance can legitimately be 0)
		// - only null/undefined (setting truly unset) should render empty
		input.value = value == null ? "" : value;
		input.addEventListener("change", () => {
			const raw = input.value;
			const num = parseFloat(raw);
			settings.set(def.key, (raw === "" || !isFinite(num)) ? 0 : num);
		});
		row.append(label, input);
	}else{
		// no other control type implemented yet - fall back to a plain label
		// rather than silently dropping the setting from view
		const span = document.createElement("span");
		span.textContent = def.label;
		row.append(span);
	}

	if(def.tooltip){
		row.append(createInfoIcon(def.tooltip));
	}

	return row;
}

// Renders one collapsible tool sub-group (e.g. "Rør"): its declared
// settings, plus - only for the "general" group - the reset-everything
// action. Only ever called for a group visibleSettingsGroups() already
// selected (see mountDigitizationSettingsPanel()) - the "does this group
// have anything to show" decision itself lives there, as a plain function
// over SETTINGS_GROUPS/SETTINGS_SCHEMA, so it's Level 1 (no DOM) testable -
// see SettingsSchema.test.js.
function renderGroup(group, onReset, viewer){
	const defs = settingsInGroup(group.id);
	const {header, content} = createCollapsibleSection(group.label, {level: "group"});

	for(const def of defs) content.append(renderControl(def));

	if(group.id === "general"){
		const resetRow = document.createElement("div");
		resetRow.className = "digitization-setting-row";
		const resetButton = document.createElement("button");
		resetButton.type = "button";
		resetButton.className = "digitization-settings-reset-btn";
		resetButton.textContent = t("Nulstil indstillinger");
		resetButton.addEventListener("click", () => {
			// Potree ejer nulstil-logikken (doReset). Bekræftelsen vises af værten (PointViews Vue-dialog:
			// Annuller | Nulstil, Esc/udenfor = annullér uden sideeffekt); uden vært (standalone Potree): native confirm().
			const doReset = () => {
				settings.reset();
				onReset();
			};
			const message = t("Nulstil alle Digitalisering-indstillinger til standardværdier?");
			if(hostDialogAvailable(viewer)){
				requestHostConfirm(viewer, {
					kind: "confirm", size: "s", title: t("Nulstil indstillinger"), text: message,
					confirmLabel: t("Nulstil"), destructive: true,
				}).then((result) => { if(result.action === "confirm") doReset(); });
			}else if(confirm(message)){
				doReset();
			}
		});
		resetRow.append(resetButton);
		content.append(resetRow);
	}

	return {header, content};
}

// Mounts (or fully re-mounts, e.g. after a reset) the Indstillinger UI into
// `container` (a raw DOM element, expected to be the sidebar's
// `#digitalisering_settings` div - or, for an external host UI, any plain
// <div>). Accepts either a raw DOM element or a jQuery-wrapped one (kept for
// backwards-compatibility with the previous sidebar call site - see
// HelpPanel.js's own mountDigitizationHelpPanel() for the same pattern).
// One top-level collapsible section - this is what makes "Indstillinger"
// itself foldable, same interaction as the other top-level Digitalisering-
// panel sections - holding one collapsible sub-group per group
// visibleSettingsGroups() returns (i.e. per group that actually has content
// - see its own doc comment), each auto-rendered from the schema so a new
// setting never requires touching this file. Never opens itself or any
// sub-group automatically - selecting a tool must not expand Indstillinger
// (task requirement); the section starts, and stays, exactly as
// collapsed/expanded as the user last left it.
// `viewer` (valgfri): bruges kun til at finde værtens dialog (viewer.pvUi) til nulstil-bekræftelsen.
export function mountDigitizationSettingsPanel(container, viewer){
	const root = (container && container.jquery) ? container[0] : container;
	root.innerHTML = "";

	const outer = createCollapsibleSection(t("Indstillinger"), {level: "section"});
	root.append(outer.header, outer.content);

	const rebuild = () => mountDigitizationSettingsPanel(container, viewer);

	for(const group of visibleSettingsGroups()){
		const {header, content} = renderGroup(group, rebuild, viewer);
		outer.content.append(header, content);
	}
}
